import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { Mail, Lock, User, ArrowRight, Phone, MapPin } from 'lucide-react';

export default function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postal_code: '',
    password: '',
    confirmPassword: '',
    newsletter_email: false,
    newsletter_sms: false
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Adgangskoderne matcher ikke');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Adgangskoden skal være mindst 6 tegn');
      return;
    }

    if (!/^\d{4}$/.test(formData.postal_code.trim())) {
      toast.error('Indtast et gyldigt 4-cifret postnummer');
      return;
    }

    setLoading(true);
    
    try {
      await register(
        formData.email,
        formData.password,
        formData.name,
        formData.phone,
        formData.newsletter_email,
        formData.newsletter_sms,
        formData.address,
        formData.postal_code
      );
      toast.success('Konto oprettet! Velkommen til Vibra');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Oprettelse fejlede. Prøv igen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-stone-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <img src="/logo.png" alt="Vibra" className="h-8 w-auto object-contain" />
            <span className="text-2xl font-bold bronze-text">Vibra</span>
          </Link>
          <h1 className="text-3xl font-bold tracking-tight">Opret konto</h1>
          <p className="text-muted-foreground mt-2">
            Opret en konto for at komme i gang
          </p>
        </div>

        <Card className="border-border/50 shadow-lg" data-testid="register-card">
          <CardHeader>
            <CardTitle>Opret brugerkonto</CardTitle>
            <CardDescription>
              Find og kontakt håndværkere nemt og hurtigt
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Fulde navn</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Dit fulde navn"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="din@email.dk"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="register-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Telefonnummer</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+45 12 34 56 78"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="register-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address">Adresse</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="address"
                    type="text"
                    placeholder="Vejnavn og husnummer"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="register-address-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="postal_code">Postnummer</Label>
                  <Input
                    id="postal_code"
                    type="text"
                    inputMode="numeric"
                    placeholder="2100"
                    value={formData.postal_code}
                    onChange={(e) => setFormData({ ...formData, postal_code: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    maxLength={4}
                    required
                    data-testid="register-postal-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city">By</Label>
                  <Input
                    id="city"
                    type="text"
                    placeholder="København Ø"
                    value={formData.city || ''}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    data-testid="register-city-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Adgangskode</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Mindst 6 tegn"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="register-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Bekræft adgangskode</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Gentag adgangskode"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className="pl-10"
                    required
                    data-testid="register-confirm-password-input"
                  />
                </div>
              </div>

              {/* Newsletter Subscription */}
              <div className="space-y-3 py-2">
                <p className="text-sm font-medium">Nyhedsbrev</p>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="newsletter-email"
                    checked={formData.newsletter_email}
                    onCheckedChange={(checked) => setFormData({ ...formData, newsletter_email: checked })}
                    data-testid="newsletter-email-checkbox"
                  />
                  <Label htmlFor="newsletter-email" className="text-sm cursor-pointer">
                    Modtag nyhedsbrev via e-mail
                  </Label>
                </div>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="newsletter-sms"
                    checked={formData.newsletter_sms}
                    onCheckedChange={(checked) => setFormData({ ...formData, newsletter_sms: checked })}
                    data-testid="newsletter-sms-checkbox"
                  />
                  <Label htmlFor="newsletter-sms" className="text-sm cursor-pointer">
                    Modtag nyhedsbrev via SMS
                  </Label>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary/90"
                disabled={loading}
                data-testid="register-submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="spinner" />
                    Opretter konto...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Opret konto
                    <ArrowRight className="w-4 h-4" />
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm">
              <p className="text-muted-foreground">
                Har du allerede en konto?{' '}
                <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
                  Log ind
                </Link>
              </p>
              <p className="text-muted-foreground mt-2">
                Er du håndværker?{' '}
                <Link to="/register-company" className="text-secondary font-medium hover:underline" data-testid="register-company-link">
                  Opret virksomhed
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
